import { useInsertionEffect } from 'react'

/**
 * Styles for the sign-in screen and the who-is-this picker.
 *
 * Component-owned CSS, injected once, in the same shape as the space screens
 * use. Every colour is a token from tokens.css — there is not a hex value in
 * this file, so dark mode falls out for free and the wall tablet stays pinned
 * to light without a second stylesheet.
 */

const STYLE_ID = 'office-auth-styles'

export const AUTH_CSS = `
/* The page behind the card: the same blue wash the phone and the wall use, so
   signing in does not look like a different product from what follows it. */
.au {
  min-height: 100dvh;
  display: grid; place-items: center;
  padding: 24px 18px calc(24px + env(safe-area-inset-bottom));
  background:
    radial-gradient(900px 520px at 100% -10%, var(--primary-soft), transparent 62%),
    linear-gradient(180deg, var(--bg) 0%, var(--bg-deep) 100%);
  background-attachment: fixed;
}

.au-card {
  width: min(420px, 100%);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  padding: 30px 28px 26px;
  display: flex; flex-direction: column; gap: 20px;
  position: relative; overflow: hidden;
}
/* A hairline of brand colour along the top edge, matching the board's tiles. */
.au-card::before {
  content: ''; position: absolute; inset: 0 0 auto 0; height: 3px;
  background: linear-gradient(90deg, var(--blue-300), var(--blue-500) 55%, var(--accent));
}

.au-head { display: flex; flex-direction: column; gap: 6px; }
.au-title { font-size: 26px; font-weight: 680; letter-spacing: -0.02em; }
.au-sub { color: var(--text-muted); font-size: 15px; }

.au-form { display: flex; flex-direction: column; gap: 16px; }
.au-field { display: flex; flex-direction: column; gap: 6px; }
.au-label { font-size: 13px; font-weight: 620; color: var(--text-muted); letter-spacing: .01em; }

.au-input {
  width: 100%;
  background: var(--surface-sunk);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 13px 14px;
  font-size: 16px;                       /* 16px or iOS zooms the page on focus */
  color: var(--text);
  transition: border-color var(--dur) var(--ease), box-shadow var(--dur) var(--ease),
              background var(--dur) var(--ease);
}
.au-input::placeholder { color: var(--text-subtle); }
.au-input:focus {
  outline: none; background: var(--surface);
  border-color: var(--primary); box-shadow: var(--ring);
}
.au-input[aria-invalid='true'] { border-color: var(--danger); }
.au-input:disabled { opacity: .6; }

/* A failed sign-in IS a genuine error, so this is the one place red belongs. */
.au-error {
  display: flex; gap: 8px; align-items: flex-start;
  background: var(--danger-soft);
  border: 1px solid var(--danger-border);
  color: var(--danger);
  border-radius: var(--radius);
  padding: 11px 13px; font-size: 14px; font-weight: 550;
}

.au-submit {
  width: 100%;
  border: 1px solid var(--blue-600);
  border-radius: var(--radius);
  background: linear-gradient(180deg, var(--blue-400), var(--blue-600));
  color: var(--text-on-primary);
  padding: 14px 16px; font-size: 16px; font-weight: 640;
  box-shadow: 0 1px 2px rgba(20, 58, 105, .18), inset 0 1px 0 rgba(255, 255, 255, .24);
  transition: background var(--dur) var(--ease), transform var(--dur) var(--ease), opacity var(--dur) var(--ease);
}
.au-submit:hover:not(:disabled) { background: var(--primary-hover); }
.au-submit:active:not(:disabled) { transform: translateY(1px); }
.au-submit:disabled { opacity: .65; cursor: default; }

.au-hints { display: flex; flex-direction: column; gap: 9px; }
.au-hints-title {
  font-size: 12px; font-weight: 640; letter-spacing: .07em; text-transform: uppercase;
  color: var(--text-subtle);
}
.au-hint-row { display: flex; flex-wrap: wrap; gap: 8px; }
.au-hint {
  display: inline-flex; align-items: baseline; gap: 6px;
  background: var(--primary-soft);
  border: 1px solid var(--primary-border);
  color: var(--blue-700);
  border-radius: 999px; padding: 7px 13px;
  font-size: 14px; font-weight: 600;
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease);
}
.au-hint:hover:not(:disabled) { background: var(--blue-100); border-color: var(--blue-300); }
.au-hint-note { font-size: 12px; font-weight: 550; color: var(--text-subtle); }

.au-foot { color: var(--text-subtle); font-size: 13px; text-align: center; }

/* ── who is this? the wall tablet's actor picker ─────────────────────────
   This one is read and tapped ON THE WALL, so it is sized from the wall's own
   scale (--wall-xs) rather than from a number typed here, and the row is given
   a target big enough to hit without aiming. */
.au-who { display: flex; flex-direction: column; gap: 16px; }
.au-who-list { display: flex; flex-direction: column; gap: 12px; }
.au-who-btn {
  display: flex; align-items: center; gap: 14px;
  width: 100%; text-align: left; min-height: 72px;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  padding: 14px 18px; font-size: var(--wall-xs); font-weight: 600;
  box-shadow: var(--shadow-sm);
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease),
              transform var(--dur) var(--ease);
}
.au-who-btn:hover { background: var(--primary-soft); border-color: var(--primary); }
.au-who-btn:active { transform: translateY(1px); }
.au-avatar {
  flex: 0 0 auto; width: 44px; height: 44px; border-radius: 999px;
  display: grid; place-items: center;
  background: var(--primary-soft); color: var(--blue-700);
  border: 1px solid var(--primary-border);
  font-size: 16px; font-weight: 680;
}

/* Who the tablet is currently acting as. Deliberately loud: an unnoticed
   "acting as" banner is how a chore ends up credited to the wrong person. */
.au-acting {
  display: flex; align-items: center; gap: 10px;
  background: var(--primary-soft);
  border: 1px solid var(--primary-border);
  border-radius: var(--radius);
  padding: 9px 12px; margin-bottom: 14px;
  font-size: 14px; color: var(--blue-700);
}
.au-acting-name { font-weight: 680; }
.au-acting-switch {
  margin-left: auto;
  background: var(--surface); border: 1px solid var(--primary-border);
  color: var(--blue-700); border-radius: 999px;
  padding: 5px 12px; font-size: 13px; font-weight: 620;
}
.au-acting-switch:hover { background: var(--blue-100); }
`

/** Injects the stylesheet once per document. Renders nothing. */
export function AuthStyles(): null {
  useInsertionEffect(() => {
    if (typeof document === 'undefined') return
    if (document.getElementById(STYLE_ID) !== null) return
    const el = document.createElement('style')
    el.id = STYLE_ID
    el.textContent = AUTH_CSS
    document.head.appendChild(el)
  }, [])
  return null
}
